import { call, put, takeLatest, delay, select, take, spawn } from 'redux-saga/effects'
import actionTypes from '../actions/actionTypes'
import appActions from '../actions/appActions'
import questionActions from '../actions/questionActions'
import settingsSelector from '../selectors/app/settingsSelector'
import projectorModeSelector from '../selectors/app/projectorModeSelector'
import userTypeSelector from '../selectors/app/userTypeSelector'
import sessionTypeSelector from '../selectors/app/sessionTypeSelector'
import playerDataSelector from '../selectors/app/playerDataSelector'
import userViewSelector from '../selectors/app/userViewSelector'
import userTypes from '../../helpers/enums/userTypes'
import viewTypes from '../../helpers/enums/views'
import gameStates from '../../helpers/enums/gameStates'
import settingsTypes from '../../helpers/enums/settings'
import settingsHelper from '../../helpers/utils/settings'
import { previewSessionTypes } from '../../helpers/enums/sessionTypes'
import timings from '../../helpers/timings/timings'
import getTTCFeatureFlags from '../helpers/featureFlagHelper'

import configLoader from '../../sync/configLoader'
import skinLoader from '../../sync/skinLoader'
import featureFlags from '../../sync/featureFlags'

function* initGame(action: any): Generator<any, any, any> {
	const sessionType = yield select(sessionTypeSelector)
	if (previewSessionTypes.includes(sessionType)) {
		yield spawn(loadDefaultSkin)
	}
	const config = yield call(configLoader.getConfig)
	yield put(appActions.configLoaded(config))

	yield call(getFeatureFlags, action.params)

	return true
}

function* getFeatureFlags(params: any): Generator<any, any, any> {
	try {
		const responseWithAllTheFeatureFlags = yield call(featureFlags.getFeatureFlags, params.jwt) // api ct call to get all ff values
		const filteredFeatureFlags = getTTCFeatureFlags(responseWithAllTheFeatureFlags.payload.feature_flags) // filter to get only ttc ff
		yield put(appActions.dispatchFeatureFlags(filteredFeatureFlags)) // action creator trigger to set the ttc ff
	} catch (error) {
		// eslint-disable-next-line no-console
		console.log(error)
	}
}

function* waitForSkin(): Generator<any, any, any> {
	while (true) {
		const action = yield take(actionTypes.GAME_UPDATE)
		if (action.payload.currentView.type !== viewTypes.SETTINGS) {
			const skinId = settingsHelper.getSettingValue(action.payload.settings, settingsTypes.SETTING_TYPE_SKIN)
			const skinData = yield call(skinLoader.getSkin, skinId)
			yield put(appActions.skinLoaded(skinData))
			break
		}
	}
}

function* loadDefaultSkin(): Generator<any, any, any> {
	const skinData = yield call(skinLoader.getSkin, '1')
	yield put(appActions.skinLoaded(skinData))
}

function* waitForGameData(): Generator<any, any, any> {
	try {
		yield take(actionTypes.GAME_UPDATE)
		const userType = yield select(userTypeSelector)
		if (userType === userTypes.TEACHER) {
			while (true) {
				const currentView = yield select(userViewSelector)
				if (currentView !== viewTypes.SETTINGS) {
					const settings = yield select(settingsSelector)
					const projectorMode = yield select(projectorModeSelector)
					const newSettings = settings.reduce((acc: any, setting: any) => {
						acc[setting.id] = setting.value
						return acc
					}, {})
					newSettings.hideStudentNames = projectorMode ? 1 : 0
					yield put(appActions.gameSettings(newSettings))
				}
				yield take(actionTypes.SESSION_SETTINGS_HIDE_STUDENT_NAMES_UPDATE)
			}
		}
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error(error)
	}
}

function* listenToQuestionTransitions(): Generator<any, any, any> {
	while (true) {
		yield take(actionTypes.GAME_UPDATE)
		const playerData = yield select(playerDataSelector)
		if (
			(playerData.view.type === viewTypes.GAME || playerData.view.type === viewTypes.WINNERS) &&
			playerData.view.payload &&
			playerData.view.payload.state === gameStates.TRANSITIONING
		) {
			yield delay((timings.questionReveal + timings.questionTransition) * 1000)
			yield put(questionActions.transitionEnded(playerData.deviceUid))
		}
	}
}

export default [
	takeLatest(actionTypes.APP_INIT, initGame),
	waitForSkin(),
	waitForGameData(),
	listenToQuestionTransitions(),
]
